import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { LogOut, Wrench, Ticket as TicketIcon, FileText, Bell } from 'lucide-react';

import AdminTicketsTab from '@/components/AdminTicketsTab';
import AdminQuotesTab from '@/components/AdminQuotesTab';
import StaffActivityStrip from '@/components/StaffActivityStrip';
import NotificationPrefsDialog from '@/components/NotificationPrefsDialog';
import NotificationBell from '@/components/NotificationBell';
import { getPermissions } from '@/lib/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Technician Dashboard.
 *
 * Limited-scope staff workspace for technicians:
 *   - Tickets management (create on behalf of clients, reply, change status)
 *   - Quotes (create, send, view audit) — financial actions (delete, convert-to-invoice)
 *     are admin-only and are hidden / 403 on the backend.
 *
 * Anyone with role !== 'technician' gets redirected away (admins go to /admin,
 * clients go to /dashboard).
 */
const TechDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [showPrefs, setShowPrefs] = useState(false);
  const [authState, setAuthState] = useState(location.state?.user ? 'ok' : 'pending');

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        const me = res.data;
        if (me.role === 'admin') {
          navigate('/admin', { replace: true, state: { user: me } });
          return;
        }
        if (me.role !== 'technician') {
          navigate('/dashboard', { replace: true, state: { user: me } });
          return;
        }
        setUser(me);
        setAuthState('ok');
      } catch (e) {
        navigate('/login', { replace: true });
      }
    };

    if (!user) {
      checkAuth();
    } else if (user.role === 'admin') {
      navigate('/admin', { replace: true, state: { user } });
    } else if (user.role !== 'technician') {
      navigate('/dashboard', { replace: true, state: { user } });
    } else {
      setAuthState('ok');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (authState !== 'ok') return;
    const fetchUsers = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/users`, { withCredentials: true });
        setUsers(res.data);
      } catch (e) {
        // Technicians have GET /admin/users access by design; if it fails, the tabs still mostly work.
      }
    };
    fetchUsers();
    fetchTickets();
  }, [authState]);

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tickets`, { withCredentials: true });
      setTickets(res.data || []);
    } catch (e) {
      // Non-fatal — keep tickets [] so the tab renders an empty state.
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out');
      navigate('/');
    } catch (e) {
      toast.error('Logout failed');
    }
  };

  if (authState !== 'ok' || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground font-mono text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="tech-dashboard">
      <header className="sticky top-0 z-50 w-full border-b border-border backdrop-blur-xl bg-background/80">
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex h-14 sm:h-16 items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="font-black text-lg sm:text-xl uppercase tracking-tight">
                NEXT<span className="text-primary">/</span>GEN
              </div>
              <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border border-blue-500/40 bg-blue-500/10 text-[10px] font-mono uppercase tracking-widest text-blue-400">
                <Wrench className="w-3 h-3" />
                Tech
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs sm:text-sm text-muted-foreground hidden sm:inline truncate max-w-[200px]">
                {user.email}
              </span>
              <NotificationBell testIdPrefix="tech" />
              <Button variant="ghost" size="sm" onClick={() => setShowPrefs(true)} data-testid="tech-prefs-btn" title="Notification preferences">
                <Bell className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Notifications</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="tech-logout-btn">
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>
      <NotificationPrefsDialog open={showPrefs} onClose={() => setShowPrefs(false)} />

      <main className="container mx-auto px-4 sm:px-6 md:px-12 py-6 sm:py-10">
        <div className="mb-6 sm:mb-10">
          <h1 className="text-2xl sm:text-4xl font-black uppercase mb-1">Tech Workspace</h1>
          <p className="text-sm text-muted-foreground">Manage tickets and quotes for clients.</p>
        </div>

        <StaffActivityStrip />

        <Card className="p-2 sm:p-4 border-border">
          <Tabs defaultValue="tickets" className="w-full">
            <TabsList className="grid w-full grid-cols-2 max-w-md mb-4 sm:mb-6">
              <TabsTrigger value="tickets" data-testid="tech-tab-tickets">
                <TicketIcon className="w-4 h-4 mr-2" />Tickets
              </TabsTrigger>
              <TabsTrigger value="quotes" data-testid="tech-tab-quotes">
                <FileText className="w-4 h-4 mr-2" />Quotes
              </TabsTrigger>
            </TabsList>

            <TabsContent value="tickets">
              <AdminTicketsTab
                tickets={tickets}
                users={users}
                onRefresh={fetchTickets}
                permissions={getPermissions(user)}
                currentUserEmail={user?.email}
              />
            </TabsContent>

            <TabsContent value="quotes">
              <AdminQuotesTab users={users} onRefresh={() => {}} permissions={getPermissions(user)} />
            </TabsContent>
          </Tabs>
        </Card>
      </main>
    </div>
  );
};

export default TechDashboard;
